import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiShoppingCart, FiDollarSign, FiGrid, FiUsers, FiPercent,
  FiWifi, FiWifiOff, FiRefreshCw, FiSettings, 
  FiChevronRight, FiCoffee, FiPlusCircle, FiTruck, FiBook,
  FiPackage, FiTrash2, FiDroplet, FiBarChart2, FiDownload
} from 'react-icons/fi';
import { useSocket } from './Layout';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

// Helper function to get axios config with auth token
const getAuthConfig = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

// Default quick actions config
const defaultQuickActions = [
  { id: 'create-order', label: 'Tạo đơn', icon: 'FiPlusCircle', color: 'green', path: '/orders', state: { openCreateModal: true }, enabled: true },
  { id: 'expenses', label: 'Chi tiêu', icon: 'FiDollarSign', color: 'blue', path: '/inventory/expenses', enabled: true },
  { id: 'products', label: 'Sản phẩm', icon: 'FiCoffee', color: 'amber', path: '/menu', enabled: true },
  { id: 'categories', label: 'Danh mục', icon: 'FiGrid', color: 'indigo', path: '/categories', enabled: true },
  { id: 'recipes', label: 'Công thức', icon: 'FiBook', color: 'purple', path: '/inventory/recipes', enabled: true },
  { id: 'discounts', label: 'Khuyến mãi', icon: 'FiPercent', color: 'red', path: '/discounts', enabled: true },
  { id: 'ingredients', label: 'Nguyên liệu', icon: 'FiPackage', color: 'teal', path: '/inventory/ingredients', enabled: false },
  { id: 'wasted', label: 'Hàng hủy', icon: 'FiTrash2', color: 'rose', path: '/inventory/wasted', enabled: false },
  { id: 'brewing', label: 'Ủ nguyên liệu', icon: 'FiDroplet', color: 'cyan', path: '/inventory/ingredients', state: { openBrewingModal: true }, enabled: false },
  { id: 'customers', label: 'Khách hàng', icon: 'FiUsers', color: 'pink', path: '/customers', enabled: false },
  { id: 'reports', label: 'Báo cáo', icon: 'FiBarChart2', color: 'emerald', path: '/reports', enabled: false },
  { id: 'stock-import', label: 'Nhập hàng', icon: 'FiDownload', color: 'lime', path: '/inventory/expenses', state: { openImportModal: true }, enabled: false },
  { id: 'add-expense', label: 'Thêm chi phí', icon: 'FiDollarSign', color: 'orange', path: '/inventory/expenses', state: { openExpenseModal: true }, enabled: false },
  { id: 'settings', label: 'Cài đặt', icon: 'FiSettings', color: 'gray', path: '/settings', enabled: true },
];

// Icon mapping
const iconComponents = {
  FiPlusCircle, FiDollarSign, FiCoffee, FiGrid, FiBook, FiPercent,
  FiPackage, FiTrash2, FiDroplet, FiUsers, FiBarChart2, FiSettings, FiDownload
};

// Color mapping for Tailwind classes
const colorClasses = {
  green: { bg: 'bg-green-50', text: 'text-green-600' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-600' },
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-600' },
  red: { bg: 'bg-red-50', text: 'text-red-600' },
  teal: { bg: 'bg-teal-50', text: 'text-teal-600' },
  rose: { bg: 'bg-rose-50', text: 'text-rose-600' },
  cyan: { bg: 'bg-cyan-50', text: 'text-cyan-600' },
  pink: { bg: 'bg-pink-50', text: 'text-pink-600' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
  lime: { bg: 'bg-lime-50', text: 'text-lime-600' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-600' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-600' },
};

// Helper function to display table name
const getTableDisplayName = (order) => {
  if (order.table?.name) return order.table.name;
  if (order.tableNumber) return `Bàn ${order.tableNumber}`;
  return 'Mang đi';
};

function Dashboard() {
  const navigate = useNavigate();
  const { socket, socketConnected } = useSocket();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [shopInfo, setShopInfo] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [debtStats, setDebtStats] = useState({ totalDebt: 0, customerCount: 0 });
  const [importStats, setImportStats] = useState({ totalCost: 0, totalImports: 0 });
  const [quickActions, setQuickActions] = useState([]);

  // Get user role
  const userRole = localStorage.getItem('userRole') || 'admin';
  const isSalesRole = userRole === 'sales';

  useEffect(() => {
    loadDashboardData();
    loadShopInfo();
    checkNotificationPermission();
    loadQuickActions();
    
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load quick actions from localStorage
  const loadQuickActions = () => {
    try {
      const saved = localStorage.getItem('quickActionsSettings');
      let actions;
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with defaults to handle new features
        const merged = defaultQuickActions.map(defaultAction => {
          const savedAction = parsed.find(s => s.id === defaultAction.id);
          return savedAction ? { ...defaultAction, enabled: savedAction.enabled } : defaultAction;
        });
        // Sort by saved order
        merged.sort((a, b) => {
          const aIndex = parsed.findIndex(s => s.id === a.id);
          const bIndex = parsed.findIndex(s => s.id === b.id);
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
        actions = merged.filter(a => a.enabled);
      } else {
        actions = defaultQuickActions.filter(a => a.enabled);
      }
      
      // Filter out reports for sales role
      if (userRole === 'sales') {
        actions = actions.filter(a => a.id !== 'reports');
      }
      
      setQuickActions(actions);
    } catch (e) {
      console.error('Error loading quick actions:', e);
      let actions = defaultQuickActions.filter(a => a.enabled);
      if (userRole === 'sales') {
        actions = actions.filter(a => a.id !== 'reports');
      }
      setQuickActions(actions);
    }
  };

  useEffect(() => {
    if (socket) {
      const handleNewOrder = () => loadDashboardData();
      const handleOrderUpdated = () => loadDashboardData();
      const handlePaymentCompleted = () => loadDashboardData();
      
      socket.on('new-order', handleNewOrder);
      socket.on('order-updated', handleOrderUpdated);
      socket.on('payment-completed', handlePaymentCompleted);
      
      return () => {
        socket.off('new-order', handleNewOrder);
        socket.off('order-updated', handleOrderUpdated);
        socket.off('payment-completed', handlePaymentCompleted);
      };
    }
  }, [socket]);

  const loadShopInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/profile`);
      setShopInfo(res.data.data);
    } catch (error) {
      console.log('Could not load shop info');
    }
  };

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') {
        toast.success('Đã bật thông báo!');
      }
    }
  };

  const navigateToOrder = (orderId) => {
    navigate('/orders', { state: { highlightOrderId: orderId } });
  };

  const loadDashboardData = async () => {
    try {
      const config = getAuthConfig();
      const [statsRes, ordersRes, debtRes, importRes] = await Promise.all([
        axios.get(`${API_URL}/admin/dashboard/stats`, config),
        axios.get(`${API_URL}/admin/dashboard/recent-orders?limit=5`, config),
        axios.get(`${API_URL}/customers/stats/debt`, config),
        axios.get(`${API_URL}/stock-imports/stats/today`, config)
      ]);

      setStats(statsRes.data.data);
      setRecentOrders(ordersRes.data.data);
      setDebtStats(debtRes.data.data || { totalDebt: 0, customerCount: 0 });
      setImportStats(importRes.data.data || { totalCost: 0, totalImports: 0 });
    } catch (error) {
      toast.error('Không thể tải dữ liệu dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN').format(Math.round(amount || 0));
  };

  const formatCurrencyFull = (amount) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(Math.round(amount || 0));
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      preparing: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      paid: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    
    const labels = {
      pending: 'Chờ xác nhận',
      preparing: 'Đang làm',
      completed: 'Hoàn thành',
      paid: 'Đã thanh toán',
      cancelled: 'Đã hủy'
    };

    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badges[status] || badges.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Format today's date
  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear().toString().slice(-2)}`;

  // Pending orders count
  const pendingOrdersCount = recentOrders.filter(o => o.status === 'pending' || o.status === 'preparing').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-3 text-gray-600 text-sm">Đang tải...</p>
        </div>
      </div>
    );
  }

  // Mobile Layout - App Style
  if (isMobile) {
    return (
      <div className="-m-6 bg-gray-50 pb-4">
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white px-4 pt-3 pb-6 rounded-b-3xl">
          {/* Top bar */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              {/* Avatar */}
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                {shopInfo?.avatar ? (
                  <img src={shopInfo.avatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  <FiCoffee className="text-white" size={20} />
                )}
              </div>
              <div>
                <p className="text-white/80 text-xs">Xin chào</p>
                <p className="font-semibold text-sm">{shopInfo?.name || 'Green Cafe'}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Refresh */}
              <button 
                onClick={loadDashboardData}
                className="p-2 rounded-full bg-white/20"
              >
                <FiRefreshCw size={16} />
              </button>
            </div>
          </div>

          {/* Stats Card */}
          <div className="bg-white rounded-xl p-3 text-gray-800 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-medium text-gray-600">HÔM NAY, {formattedDate}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-0.5">Doanh thu</p>
                <p className="font-bold text-base text-gray-800">{formatCurrency(stats?.todayRevenue || 0)}</p>
              </div>
              <div className="text-center border-x border-gray-100">
                <p className="text-xs text-gray-500 mb-0.5">Đơn hàng</p>
                <p className="font-bold text-base text-gray-800">{stats?.todayOrders || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-0.5">Bàn đang dùng</p>
                <p className="font-bold text-base text-gray-800">{stats?.occupiedTables || 0}/{stats?.totalTables || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="px-4 py-4">
          <p className="text-sm font-semibold text-gray-700 mb-3">Tính năng cho bạn</p>
          
          <div className="grid grid-cols-3 gap-3">
            {quickActions.map(action => {
              const IconComponent = iconComponents[action.icon] || FiSettings;
              const colors = colorClasses[action.color] || colorClasses.gray;
              
              return (
                <button 
                  key={action.id}
                  onClick={() => navigate(action.path, action.state ? { state: action.state } : undefined)}
                  className="bg-white rounded-xl p-3 shadow-sm flex flex-col items-center active:bg-gray-50"
                >
                  <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center mb-2`}>
                    <IconComponent className={colors.text} size={20} />
                  </div>
                  <span className="text-xs text-gray-700 font-medium">{action.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="px-4 pb-4">
          <p className="text-sm font-semibold text-gray-700 mb-3">Việc cần làm</p>
          
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Đơn đang xử lý */}
            <button 
              onClick={() => navigate('/orders')}
              className="w-full flex items-center justify-between p-3 border-b border-gray-50 active:bg-gray-50"
            >
              <div className="flex items-center space-x-3">
                <span className="text-lg">📋</span>
                <span className="text-sm text-gray-700">Đơn đang xử lý</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-semibold text-gray-800">{pendingOrdersCount}</span>
                <FiChevronRight className="text-gray-400" size={16} />
              </div>
            </button>
            
            {/* Bàn đang sử dụng */}
            <button 
              onClick={() => navigate('/tables')}
              className="w-full flex items-center justify-between p-3 active:bg-gray-50"
            >
              <div className="flex items-center space-x-3">
                <span className="text-lg">🪑</span>
                <span className="text-sm text-gray-700">Bàn đang sử dụng</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-semibold text-gray-800">{stats?.occupiedTables || 0}</span>
                <FiChevronRight className="text-gray-400" size={16} />
              </div>
            </button>
          </div>
        </div>

        {/* Recent Orders - Compact */}
        <div className="px-4 pb-20">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-700">Đơn hàng gần đây</p>
            <button 
              onClick={() => navigate('/orders')}
              className="text-xs text-green-600 font-medium"
            >
              Xem tất cả
            </button>
          </div>
          
          <div className="space-y-2">
            {recentOrders.slice(0, 3).map((order) => (
              <button
                key={order._id}
                onClick={() => navigateToOrder(order._id)}
                className="w-full bg-white rounded-xl p-3 shadow-sm text-left active:bg-gray-50"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-gray-800">
                    {getTableDisplayName(order)}
                  </span>
                  {getStatusBadge(order.status)}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {order.items.length} món • {new Date(order.createdAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className="text-sm font-semibold text-green-600">
                    {formatCurrency(order.totalAmount)}đ
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Desktop Layout - Keep existing style but optimized
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadDashboardData}
            className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
          >
            <FiRefreshCw size={16} />
            <span>Làm mới</span>
          </button>
          <div className={`flex items-center space-x-2 ${socketConnected ? 'text-green-600' : 'text-red-600'}`}>
            {socketConnected ? <FiWifi size={18} /> : <FiWifiOff size={18} />}
            <span className="text-sm font-medium">
              {socketConnected ? 'Đang hoạt động' : 'Mất kết nối'}
            </span>
          </div>
        </div>
      </div>

      {notificationPermission === 'denied' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-800 text-sm">
            ⚠️ Thông báo bị chặn. Vui lòng bật trong cài đặt trình duyệt.
          </p>
        </div>
      )}

      {/* Stats Cards */}
      <div className={`grid grid-cols-2 ${isSalesRole ? 'lg:grid-cols-3' : 'lg:grid-cols-6'} gap-4`}>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs">Đơn hàng hôm nay</p>
              <p className="text-xl font-bold text-gray-800 mt-1">{stats?.todayOrders || 0}</p>
            </div>
            <div className="bg-blue-100 p-2.5 rounded-lg">
              <FiShoppingCart className="text-blue-600" size={20} />
            </div>
          </div>
        </div>

        {/* Doanh thu - Ẩn với role sales */}
        {!isSalesRole && (
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-xs">Doanh thu hôm nay</p>
                <p className="text-xl font-bold text-green-600 mt-1">{formatCurrencyFull(stats?.todayRevenue || 0)}</p>
              </div>
              <div className="bg-green-100 p-2.5 rounded-lg">
                <FiDollarSign className="text-green-600" size={20} />
              </div>
            </div>
          </div>
        )}

        {/* Chi phí nhập - Ẩn với role sales */}
        {!isSalesRole && (
          <div 
            className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/inventory/imports')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-xs">Chi phí nhập hôm nay</p>
                <p className={`text-xl font-bold mt-1 ${importStats.totalCost > 0 ? 'text-red-600' : 'text-gray-800'}`}>
                  {formatCurrencyFull(importStats.totalCost || 0)}
                </p>
                {importStats.totalImports > 0 && (
                  <p className="text-xs text-gray-500">{importStats.totalImports} phiếu nhập</p>
                )}
              </div>
              <div className={`p-2.5 rounded-lg ${importStats.totalCost > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                <FiTruck className={importStats.totalCost > 0 ? 'text-red-600' : 'text-gray-600'} size={20} />
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs">Bàn đang sử dụng</p>
              <p className="text-xl font-bold text-gray-800 mt-1">{stats?.occupiedTables || 0}/{stats?.totalTables || 0}</p>
            </div>
            <div className="bg-purple-100 p-2.5 rounded-lg">
              <FiGrid className="text-purple-600" size={20} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs">Tổng món ăn</p>
              <p className="text-xl font-bold text-gray-800 mt-1">{stats?.totalMenuItems || 0}</p>
            </div>
            <div className="bg-yellow-100 p-2.5 rounded-lg">
              <FiCoffee className="text-yellow-600" size={20} />
            </div>
          </div>
        </div>

        {/* Khách nợ - Ẩn với role sales */}
        {!isSalesRole && (
          <div 
            className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/customers', { state: { filterDebt: true } })}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-xs">Khách đang nợ</p>
                <p className={`text-xl font-bold mt-1 ${debtStats.totalDebt > 0 ? 'text-orange-600' : 'text-gray-800'}`}>
                  {formatCurrencyFull(debtStats.totalDebt || 0)}
                </p>
                {debtStats.customerCount > 0 && (
                  <p className="text-xs text-gray-500">{debtStats.customerCount} khách hàng</p>
                )}
              </div>
              <div className={`p-2.5 rounded-lg ${debtStats.totalDebt > 0 ? 'bg-orange-100' : 'bg-gray-100'}`}>
                <FiUsers className={debtStats.totalDebt > 0 ? 'text-orange-600' : 'text-gray-600'} size={20} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">Đơn hàng gần đây</h2>
          <button 
            onClick={() => navigate('/orders')}
            className="text-sm text-green-600 hover:underline"
          >
            Xem tất cả
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Bàn</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Món</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Tổng tiền</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Trạng thái</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Thời gian</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {recentOrders.map((order) => (
                <tr 
                  key={order._id} 
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigateToOrder(order._id)}
                >
                  <td className="px-4 py-3 whitespace-nowrap font-semibold text-sm">
                    {getTableDisplayName(order)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs">
                      {order.items.slice(0, 2).map((item, idx) => (
                        <div key={idx}>
                          {item.name} x{item.quantity}
                        </div>
                      ))}
                      {order.items.length > 2 && (
                        <div className="text-gray-500">+{order.items.length - 2} món khác</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap font-semibold text-sm text-green-600">
                    {formatCurrencyFull(order.totalAmount)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {getStatusBadge(order.status)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                    {new Date(order.createdAt).toLocaleString('vi-VN')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
